<script lang="ts">
	import {
		addDollarSign,
		getDecimalOfToken,
		getNumBackInUnits,
		getPrecisionOfToken
	} from '$lib/util/util';

	import {
		processingFundWithdraw,
		smartContractWalletAddress,
		withdrawFundsInsufficientBalance,
		withdrawFundsTokenAmount,
		withdrawToken,
		withdrawFundsAmount
	} from '../store';
	import { getUserBalances } from '$lib/data/main';

	import {
		addErrorNotification,
		addTransactionNotification
	} from '$lib/notifications/notificationManager';

	import { sanitizeInputs, toBN } from '$lib/util/util';
	import type { SendTransactionResult } from '@wagmi/core';
	import { userBalances, userBalancesEnriched } from '../../store';
	import { getParsedError } from '$lib/web3/errorHandling';
	import type { TokenSymbol } from '$lib/types/tokens';
	import { tokensAllowedForWithdraw } from '../config';
	import { optimismTokensConfig } from '$lib/tokensConfig';
	import { withdrawTokenFromSCW } from '$lib/web3/actions/scwModifiers';
	import { walletAddress } from '$lib/wallet/store';
	import ScTokenDropDown from '../utils/SCTokenDropDown.svelte';
	import ScTokenSelector from '../utils/SCTokenSelector.svelte';
	import ScButton from '../utils/SCButton.svelte';
	import { ethers } from 'ethers';
	import { getStablesPrices } from '$lib/data/matchaCallData';

	let showTokenSelector: boolean = false;
	let withdrawInput: string = '';

	function withdrawInputChanged() {
		withdrawInput = sanitizeInputs(withdrawInput);
		if (withdrawInput) {
			$withdrawFundsTokenAmount = toBN(withdrawInput, getDecimalOfToken($withdrawToken));
		} else $withdrawFundsTokenAmount = toBN('0');
	}

	function setMaxWithdrawInput() {
		$withdrawFundsTokenAmount = ethers.BigNumber.from(
			$userBalancesEnriched[$withdrawToken].balance
		);
		withdrawInput = getNumBackInUnits(
			$withdrawFundsTokenAmount.toString(),
			getDecimalOfToken($withdrawToken),
			getPrecisionOfToken($withdrawToken)
		);
	}

	async function executeWithdrawFunds() {
		try {
			$processingFundWithdraw = true;
			let tx: SendTransactionResult = await withdrawTokenFromSCW(
				optimismTokensConfig[$withdrawToken].address,
				$smartContractWalletAddress as `0x${string}`,
				$withdrawFundsTokenAmount,
				$walletAddress as `0x${string}`
			);
			addTransactionNotification(
				'Funds Withdrawn',
				`${withdrawInput} ${$withdrawToken} have been transfered from Polynomial wallet to your wallet.`,
				tx,
				() => {
					withdrawInput = '0';
					$withdrawFundsTokenAmount = toBN('0');
					getUserBalances();
					getStablesPrices($userBalances);
					$processingFundWithdraw = false;
				}
			);
			$processingFundWithdraw = false;
		} catch (e: any) {
			const { title, description } = getParsedError(e);
			addErrorNotification(title, description, JSON.stringify(e));
			$processingFundWithdraw = false;
		}
	}

	function changeToken(token: TokenSymbol) {
		$withdrawToken = token;
		showTokenSelector = false;
		$withdrawFundsTokenAmount = toBN('0');
		withdrawInput = '';
	}
</script>

<ScTokenDropDown {changeToken} {showTokenSelector} tokensAllowed={tokensAllowedForWithdraw} />
<div>
	<div class="mt-3 overflow-visible">
		<div
			class="flex justify-between border-fx-grey-500 border rounded-xl squircle-xl px-5 py-4 items-center w-full overflow-visible"
		>
			<div class=" items-end gap-1">
				<input
					inputmode="decimal"
					autocomplete="off"
					autocorrect="off"
					minlength="1"
					maxlength="79"
					spellcheck="false"
					pattern="^[0-9]*[.,]?[0-9]*$"
					bind:value={withdrawInput}
					on:input={withdrawInputChanged}
					type="string"
					placeholder="0"
					class="bg-transparent text-fx-white font-medium outline-none text-2xl max-w-[130px]"
				/>
				{#if withdrawInput}
					<div class="text-fx-grey-400 text-xs">
						{addDollarSign(getNumBackInUnits($withdrawFundsAmount.toString(), 18, 2))}
					</div>
				{:else}
					<div class="text-fx-grey-400 text-xs">-</div>
				{/if}
			</div>

			<div class="">
				<ScTokenSelector bind:showTokenSelector token={$withdrawToken} />

				<div class="text-fx-grey-400 text-xs ml-auto mr-2 flex gap-1.5">
					Balance:
					{#if $userBalancesEnriched && $userBalancesEnriched[$withdrawToken]}
						<span class="text-fx-grey-200 text-xs"
							>{getNumBackInUnits(
								$userBalancesEnriched[$withdrawToken].balance,
								getDecimalOfToken($withdrawToken),
								getPrecisionOfToken($withdrawToken)
							)}</span
						>
						{#if withdrawInput !== getNumBackInUnits($userBalancesEnriched[$withdrawToken].balance, 18, 2)}
							<button
								class="cursor-pointer text-xs text-fx-blue underline"
								on:click={setMaxWithdrawInput}
								on:keydown={setMaxWithdrawInput}
							>
								Max
							</button>
						{/if}
					{:else}
						<span class="text-fx-grey-200 text-xs">--</span>
					{/if}
				</div>
			</div>
		</div>

		<ScButton
			input={withdrawInput}
			fundsAmount={$withdrawFundsAmount}
			validation={$withdrawFundsInsufficientBalance}
			processing={$processingFundWithdraw}
			execute={executeWithdrawFunds}
			type={'Withdraw'}
		/>
	</div>
</div>
